import {
    type CSSProperties,
    type UIEvent,
    useState
} from "react";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    type TabBarLabelBehaviour,
    type TabBarIconAnimation,
    type TabBarItemType,
    NCoreUIKitTheme,
    TabBar,
    Text
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    type DocsStyleTokens,
    BulletList,
    InlineCode,
    Paragraph,
    CodeBlock,
    DemoFrame,
    Article,
    Preview,
    Section
} from "../../components/content";
import {
    SearchIcon,
    HeartIcon,
    HomeIcon,
    UserIcon,
    FileIcon,
    InfoIcon,
    BellIcon
} from "../../anatomy/icons";

const NAME = "TabBar";

const USAGE_CODE = `const [activeKey, setActiveKey] = useState("home");

<TabBar
    items={[
        {
            icon: HomeIcon,
            title: "Home",
            key: "home"
        },
        {
            icon: BellIcon,
            title: "Alerts",
            key: "alerts",
            badge: 3
        },
        {
            icon: UserIcon,
            title: "Profile",
            key: "profile",
            badge: "dot"
        }
    ]}
    onChange={setActiveKey}
    activeKey={activeKey}
/>`;

const HIDE_CODE = `const {
    onScroll,
    isHidden
} = TabBar.useScroll();

<ScrollView
    scrollEventThrottle={16}
    onScroll={onScroll}
/>

<TabBar
    isHidden={isHidden}
    items={items}
/>`;

type TabKey = "favourites" | "library" | "profile" | "alerts" | "search" | "home" | "help";

type TabBarDemoProps = {
    labelBehaviour?: TabBarLabelBehaviour;
    iconAnimation?: TabBarIconAnimation;
    items: Array<TabBarItemType>;
    isScreenVisible?: boolean;
    initialKey: string;
    label?: string;
};

const SCROLL_ROW_COUNT = 18;

const LABEL_BEHAVIOURS: Array<TabBarLabelBehaviour> = [
    "always",
    "selected",
    "never"
];

const getDemoStyle = ({
    spaces
}: DocsStyleTokens): CSSProperties => ({
    alignItems: "flex-start",
    flexDirection: "column",
    gap: spaces.spacingSm,
    display: "flex",
    width: "100%"
});

const getFrameStyle = ({
    shapeConfigs
}: DocsStyleTokens): CSSProperties => ({
    maxWidth: shapeConfigs.dialogMaxWidth,
    width: "100%"
});

const getScreenStyle = ({
    shapeConfigs
}: DocsStyleTokens): CSSProperties => ({
    minHeight: shapeConfigs.chartHeight.small,
    justifyContent: "center",
    alignItems: "center",
    display: "flex"
});

const SCROLL_FRAME_STYLE: CSSProperties = {
    position: "relative",
    height: "100%"
};

const getScrollBodyStyle = ({
    spaces
}: DocsStyleTokens): CSSProperties => ({
    padding: `${spaces.spacingMd}px ${spaces.spacingMd}px ${spaces.spacingXxl}px`,
    overscrollBehavior: "contain",
    flexDirection: "column",
    gap: spaces.spacingMd,
    overflowY: "auto",
    display: "flex",
    height: "100%"
});

const SCROLL_BAR_STYLE = {
    position: "absolute",
    bottom: 0,
    right: 0,
    left: 0
} as const;

const getTabs = (t: DocsTranslate): Record<TabKey, TabBarItemType> => ({
    favourites: {
        title: t("tabBarPage.favourites"),
        key: "favourites",
        icon: HeartIcon
    },
    alerts: {
        title: t("tabBarPage.alerts"),
        icon: BellIcon,
        key: "alerts"
    },
    help: {
        title: t("tabBarPage.help"),
        icon: InfoIcon,
        key: "help"
    },
    library: {
        title: t("sample.library"),
        icon: FileIcon,
        key: "library"
    },
    search: {
        title: t("sample.search"),
        icon: SearchIcon,
        key: "search"
    },
    profile: {
        title: t("sample.item1"),
        icon: UserIcon,
        key: "profile"
    },
    home: {
        title: t("sample.home"),
        icon: HomeIcon,
        key: "home"
    }
});

const TabBarDemo = ({
    isScreenVisible = false,
    initialKey,
    items,
    label,
    ...props
}: TabBarDemoProps) => {
    const docsTokens = NCoreUIKitTheme.useContext();

    const [
        activeKey,
        setActiveKey
    ] = useState(initialKey);

    const activeItem = items.find((item) => item.key === activeKey);

    return <div
        style={getDemoStyle(docsTokens)}
    >
        {
            label ?
                <InlineCode>
                    {label}
                </InlineCode>
            :
                null
        }
        <div
            style={getFrameStyle(docsTokens)}
        >
            <DemoFrame>
                {
                    isScreenVisible ?
                        <div
                            style={getScreenStyle(docsTokens)}
                        >
                            <Text
                                variant="titleMediumSize"
                                color="high"
                            >
                                {activeItem?.title}
                            </Text>
                        </div>
                    :
                        null
                }
                <TabBar
                    {...props}
                    onChange={setActiveKey}
                    activeKey={activeKey}
                    items={items}
                />
            </DemoFrame>
        </div>
    </div>;
};

const ScrollDemo = ({
    items
}: {
    items: Array<TabBarItemType>;
}) => {
    const docsTokens = NCoreUIKitTheme.useContext();

    const {
        t
    } = useDocsI18n();

    const {
        onScroll,
        isHidden
    } = TabBar.useScroll();

    const [
        activeKey,
        setActiveKey
    ] = useState("home");

    const handleScroll = (event: UIEvent<HTMLDivElement>) => {
        const {
            clientHeight,
            scrollHeight,
            scrollTop
        } = event.currentTarget;

        onScroll({
            nativeEvent: {
                contentSize: {
                    height: scrollHeight
                },
                layoutMeasurement: {
                    height: clientHeight
                },
                contentOffset: {
                    y: scrollTop
                }
            }
        });
    };

    return <div
        style={getFrameStyle(docsTokens)}
    >
        <DemoFrame
            height={docsTokens.shapeConfigs.chartHeight.large}
        >
            <div
                style={SCROLL_FRAME_STYLE}
            >
                <div
                    style={getScrollBodyStyle(docsTokens)}
                    onScroll={handleScroll}
                >
                    {
                        Array.from({
                            length: SCROLL_ROW_COUNT
                        }, (_, index) => <Text
                            variant="bodyMediumSize"
                            color="mid"
                            key={index}
                        >
                            {t("tabBarPage.scrollRow", [
                                index + 1
                            ])}
                        </Text>)
                    }
                </div>
                <TabBar
                    style={SCROLL_BAR_STYLE}
                    onChange={setActiveKey}
                    activeKey={activeKey}
                    isHidden={isHidden}
                    items={items}
                />
            </div>
        </DemoFrame>
    </div>;
};

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("tabBarPage.labelsTitle"),
        id: "labels"
    },
    {
        title: t("section.states"),
        id: "states"
    },
    {
        title: t("tabBarPage.overflowTitle"),
        id: "overflow"
    },
    {
        title: t("tabBarPage.hidingTitle"),
        id: "hiding"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const TabBarPage = () => {
    const {
        t
    } = useDocsI18n();

    const tabs = getTabs(t);

    const mainItems: Array<TabBarItemType> = [
        tabs.home,
        tabs.library,
        {
            ...tabs.alerts,
            badge: 3
        },
        {
            ...tabs.profile,
            badge: "dot"
        }
    ];

    const plainItems: Array<TabBarItemType> = [
        tabs.home,
        tabs.library,
        tabs.alerts,
        tabs.profile
    ];

    const badgeItems: Array<TabBarItemType> = [
        tabs.home,
        {
            ...tabs.alerts,
            badge: 120
        },
        {
            ...tabs.profile,
            badge: "dot"
        }
    ];

    const disabledItems: Array<TabBarItemType> = [
        tabs.home,
        {
            ...tabs.library,
            isDisabled: true
        },
        tabs.alerts
    ];

    const overflowItems: Array<TabBarItemType> = [
        tabs.home,
        tabs.library,
        tabs.search,
        {
            ...tabs.alerts,
            badge: 3
        },
        tabs.favourites,
        {
            ...tabs.profile,
            badge: "dot"
        },
        tabs.help
    ];

    return <Article>
        <ComponentHeader
            lead={t("tabBarPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("tabBarPage.overviewText")}
            </Paragraph>
            <Preview
                caption={t("tabBarPage.overviewCaption")}
                isStacked={true}
            >
                <TabBarDemo
                    isScreenVisible={true}
                    items={mainItems}
                    initialKey="home"
                />
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("tabBarPage.labelsTitle")}
            id="labels"
        >
            <Paragraph>
                {t("tabBarPage.labelsText")}
            </Paragraph>
            <Preview
                caption={t("tabBarPage.labelsCaption")}
                isStacked={true}
            >
                {
                    LABEL_BEHAVIOURS.map((behaviour) => <TabBarDemo
                        label={`labelBehaviour="${behaviour}"`}
                        labelBehaviour={behaviour}
                        iconAnimation="signature"
                        items={plainItems}
                        initialKey="home"
                        key={behaviour}
                    />)
                }
            </Preview>
        </Section>
        <Section
            title={t("section.states")}
            id="states"
        >
            <Paragraph>
                {t("tabBarPage.statesText")}
            </Paragraph>
            <Preview
                caption={t("tabBarPage.statesCaption")}
                isStacked={true}
            >
                <TabBarDemo
                    items={badgeItems}
                    initialKey="home"
                    label="badge"
                />
                <TabBarDemo
                    items={disabledItems}
                    label="isDisabled"
                    initialKey="home"
                />
            </Preview>
        </Section>
        <Section
            title={t("tabBarPage.overflowTitle")}
            id="overflow"
        >
            <Paragraph>
                {t("tabBarPage.overflowText")}
            </Paragraph>
            <Preview
                caption={t("tabBarPage.overflowCaption")}
                isStacked={true}
            >
                <TabBarDemo
                    isScreenVisible={true}
                    items={overflowItems}
                    initialKey="home"
                />
            </Preview>
        </Section>
        <Section
            title={t("tabBarPage.hidingTitle")}
            id="hiding"
        >
            <Paragraph>
                {t("tabBarPage.hidingText")}
            </Paragraph>
            <Preview
                caption={t("tabBarPage.hidingCaption")}
                isStacked={true}
            >
                <ScrollDemo
                    items={plainItems}
                />
            </Preview>
            <CodeBlock
                code={HIDE_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("tabBarPage.a11yLandmark"),
                    t("tabBarPage.a11yItems"),
                    t("tabBarPage.a11yCurrent"),
                    t("tabBarPage.a11yBadges"),
                    t("tabBarPage.a11yMore"),
                    t("tabBarPage.a11yKeyboard"),
                    t("tabBarPage.a11yMotion")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("tabBarPage.propItems"),
                        type: "Array<TabBarItemType>",
                        name: "items"
                    },
                    {
                        description: t("tabBarPage.propActiveKey"),
                        name: "activeKey",
                        type: "string"
                    },
                    {
                        description: t("tabBarPage.propOnChange"),
                        type: "(key: string) => void",
                        name: "onChange"
                    },
                    {
                        description: t("tabBarPage.propLabelBehaviour"),
                        type: "\"selected\" | \"always\" | \"never\"",
                        defaultValue: "\"always\"",
                        name: "labelBehaviour"
                    },
                    {
                        description: t("tabBarPage.propIconAnimation"),
                        type: "\"signature\" | \"none\"",
                        defaultValue: "\"none\"",
                        name: "iconAnimation"
                    },
                    {
                        description: t("tabBarPage.propIsHiddenOnKeyboard"),
                        name: "isHiddenOnKeyboard",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("tabBarPage.propIsHiddenOnScroll"),
                        name: "isHiddenOnScroll",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        description: t("tabBarPage.propIsHidden"),
                        name: "isHidden",
                        type: "boolean"
                    },
                    {
                        description: t("tabBarPage.propMoreTitle"),
                        name: "moreTitle",
                        type: "string"
                    },
                    {
                        description: t("tabBarPage.propTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("tabBarPage.propStyle"),
                        type: "StyleProp<ViewStyle>",
                        name: "style"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("tabBarPage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("tabBarPage.propCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("tabBarPage.propsNote")}
            </Paragraph>
            <Paragraph>
                <InlineCode>
                    TabBarItemType
                </InlineCode>
                {" "}
                {t("tabBarPage.itemText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("tabBarPage.itemKey"),
                        type: "string",
                        name: "key"
                    },
                    {
                        description: t("tabBarPage.itemTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("tabBarPage.itemIcon"),
                        type: "NCoreUIKitIcon",
                        name: "icon"
                    },
                    {
                        description: t("tabBarPage.itemBadge"),
                        type: "number | \"dot\"",
                        name: "badge"
                    },
                    {
                        description: t("tabBarPage.itemCount"),
                        type: "number",
                        name: "count"
                    },
                    {
                        description: t("tabBarPage.itemIsDisabled"),
                        name: "isDisabled",
                        type: "boolean"
                    },
                    {
                        description: t("tabBarPage.itemIsActive"),
                        name: "isActive",
                        type: "boolean"
                    },
                    {
                        description: t("tabBarPage.itemHref"),
                        type: "string",
                        name: "href"
                    },
                    {
                        description: t("tabBarPage.itemOnPress"),
                        type: "() => void",
                        name: "onPress"
                    }
                ]}
            />
            <Paragraph>
                <InlineCode>
                    TabBar.useScroll()
                </InlineCode>
                {" "}
                {t("tabBarPage.hookText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("tabBarPage.hookOnScroll"),
                        type: "({ nativeEvent }) => void",
                        name: "onScroll"
                    },
                    {
                        description: t("tabBarPage.hookIsHidden"),
                        name: "isHidden",
                        type: "boolean"
                    }
                ]}
            />
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default TabBarPage;
